import { environment } from '@env/environment';
import { UserRole } from '@security/permissions/models/permissions';

interface PostLoginCandidate {
  path: string;
  permissionData: readonly unknown[];
  activeArea: string;
}

/** Historical defaults: checked before any other permitted route. */
const PRIMARY_DEFAULT_POST_LOGIN_CANDIDATES: readonly PostLoginCandidate[] = [
  {
    path: '/market-messenger/dip/dashboard',
    permissionData: ['UnityMessengerDashboard'],
    activeArea: 'Messenger',
  },
  {
    path: '/market-messenger/dtn/search-flows',
    permissionData: ['DTNMessengerSearchFlows'],
    activeArea: 'Messenger',
  },
  {
    path: '/message-vault/search',
    permissionData: ['MessageVaultSearch'],
    activeArea: 'Message Vault',
  },
  {
    path: '/reporting/manage-reports',
    permissionData: ['ReportManagement'],
    activeArea: 'Reports',
  },
  {
    path: '/settings/user-management',
    permissionData: ['UserManagement'],
    activeArea: 'Settings',
  },
];

/** Other navigation entries when the user lacks access to the primary defaults. */
const FALLBACK_DEFAULT_POST_LOGIN_CANDIDATES: readonly PostLoginCandidate[] = [
  {
    path: '/market-messenger/dip/billing',
    permissionData: ['MessengerDIPBilling'],
    activeArea: 'Messenger',
  },
  {
    path: '/market-messenger/dip/service-plans',
    permissionData: ['MessengerDIPServicePlanManagement'],
    activeArea: 'Messenger',
  },
  {
    path: '/market-messenger/dip/message-audit-reports',
    permissionData: ['MessageAuditReport'],
    activeArea: 'Messenger',
  },
  {
    path: '/market-messenger/dtn/manage-flow-jobs',
    permissionData: ['DTNMessengerManageMessages'],
    activeArea: 'Messenger',
  },
  {
    path: '/market-messenger/dtn/flow-builder',
    permissionData: [{ permissionName: 'DTNMessengerFlowBuilder', permissionTypes: ['WriteAccess'] }],
    activeArea: 'Messenger',
  },
  {
    path: '/market-messenger/dtn/route-config',
    permissionData: ['DTNMessengerFlowConfiguration'],
    activeArea: 'Messenger',
  },
  {
    path: '/market-messenger/dtn/reports',
    permissionData: ['DTNMessengerReports'],
    activeArea: 'Messenger',
  },
  {
    path: '/message-vault/service-plan-management',
    permissionData: ['ServicePlanManagement'],
    activeArea: 'Message Vault',
  },
  {
    path: '/message-vault/billing',
    permissionData: ['MessageVaultBilling'],
    activeArea: 'Message Vault',
  },
  {
    path: '/reporting/view-reports',
    permissionData: ['ViewReports'],
    activeArea: 'Reports',
  },
];

const DEFAULT_POST_LOGIN_CANDIDATES: readonly PostLoginCandidate[] = [
  ...PRIMARY_DEFAULT_POST_LOGIN_CANDIDATES,
  ...FALLBACK_DEFAULT_POST_LOGIN_CANDIDATES,
];

function roleSatisfiesPermissionTypes(role: UserRole, permissionTypes: string[]): boolean {
  const userPerm = role.permissions;
  if (permissionTypes.includes(userPerm)) {
    return true;
  }
  return permissionTypes.includes('ReadAccess') && userPerm === 'WriteAccess';
}

/**
 * Same rules as {@link PermissionsService} navigation permission checks.
 */
export function userMatchesNavigationPermissionData(
  permissionData: readonly unknown[],
  userRoles: UserRole[]
): boolean {
  if (!permissionData?.length || !userRoles?.length) {
    return false;
  }
  return permissionData.some((perm) => {
    if (typeof perm === 'string') {
      return !!perm && userRoles.some((role) => role.roleName === perm);
    }
    const permissionName = (perm as { permissionName?: string })?.permissionName;
    const permissionTypes: string[] | undefined = (perm as { permissionTypes?: string[] })?.permissionTypes;
    if (!permissionName) {
      return false;
    }
    const matchingRoles = userRoles.filter((role) => role.roleName === permissionName);
    if (matchingRoles.length === 0) {
      return false;
    }
    if (!permissionTypes?.length) {
      return true;
    }
    return matchingRoles.some((role) => roleSatisfiesPermissionTypes(role, permissionTypes));
  });
}

/**
 * Resolves the first default app URL the user may open, or empty string if none matched.
 */
export function resolveDefaultPostLoginPath(
  userRoles: UserRole[],
  activeAreas: readonly string[] = environment.activeAreas
): string {
  for (const candidate of DEFAULT_POST_LOGIN_CANDIDATES) {
    if (!activeAreas.includes(candidate.activeArea)) {
      continue;
    }
    if (userMatchesNavigationPermissionData(candidate.permissionData, userRoles)) {
      return candidate.path;
    }
  }
  return '';
}
